import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutWrapper from '../components/LayoutWrapper';
import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import config from '../config';
import Helper from '../helpers';

const baseUrl = config.apiBaseUrl;

const VenueAttendanceReport = () => {
  const navigate = useNavigate();
  const [venueKey, setVenueKey] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get venue key from token claims
    const getVenueKeyFromToken = async () => {
      const token = await Helper.getCookie('wh-jwt');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const adminVenueClaim = payload.admin_venue;
          if (adminVenueClaim) {
            setVenueKey(adminVenueClaim);
          }
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
    };

    getVenueKeyFromToken();

    // Set default date range to last 3 months
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 3);

    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  const fetchVenueAttendanceReport = useCallback(async () => {
    if (!startDate || !endDate) {
      setError('Please select a date range');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await Helper.getCookie('wh-jwt');
      const response = await fetch(
        `${baseUrl}/api/venues/${venueKey}/reports/venue-attendance?startDate=${startDate}&endDate=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        setError('Failed to fetch venue attendance report');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Error loading report');
    } finally {
      setLoading(false);
    }
  }, [venueKey, startDate, endDate]);

  // Auto-refresh report when date range changes after initial report
  useEffect(() => {
    if (venueKey && startDate && endDate) {
      fetchVenueAttendanceReport();
    }
  }, [venueKey, startDate, endDate, fetchVenueAttendanceReport]);

  // Create a grid with dates at top and hours (0-23) on the left
  const attendanceGrid = useMemo(() => {
    if (!reportData || !reportData.sessionStats) return { grid: [], dates: [], hours: [] };

    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates = [];

    // Generate all dates in range
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }

    // Create hours array (0-23)
    const hours = Array.from({ length: 24 }, (_, i) => i);

    // Create a map of date+hour to session count
    const sessionsByDateHour = {};
    reportData.sessionStats.forEach(session => {
      const sessionDate = new Date(session.date);
      const dateStr = sessionDate.toISOString().split('T')[0];
      const hour = sessionDate.getHours();
      const key = `${dateStr}-${hour}`;

      if (!sessionsByDateHour[key]) {
        sessionsByDateHour[key] = {
          count: 0,
          attendees: 0,
          tokens: 0,
          sessions: []
        };
      }

      sessionsByDateHour[key].count += 1;
      sessionsByDateHour[key].attendees += session.attendes;
      sessionsByDateHour[key].tokens += session.tokenSpent;
      sessionsByDateHour[key].sessions.push(session);
    });

    // Create the grid
    const grid = hours.map(hour => {
      return dates.map(date => {
        const dateStr = date.toISOString().split('T')[0];
        const key = `${dateStr}-${hour}`;
        return sessionsByDateHour[key] || { count: 0, attendees: 0, tokens: 0, sessions: [] };
      });
    });

    return { grid, dates, hours };
  }, [reportData, startDate, endDate]);

  const getIntensityColor = (count) => {
    if (count === 0) return 'bg-gray-800';
    if (count === 1) return 'bg-blue-900';
    if (count === 2) return 'bg-blue-700';
    if (count === 3) return 'bg-blue-500';
    return 'bg-blue-400';
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12am';
    if (hour < 12) return `${hour}am`;
    if (hour === 12) return '12pm';
    return `${hour - 12}pm`;
  };

  // Get month labels
  const monthLabels = useMemo(() => {
    if (attendanceGrid.dates.length === 0) return [];

    const labels = [];
    let lastMonth = -1;

    attendanceGrid.dates.forEach((date, index) => {
      const month = date.getMonth();
      if (month !== lastMonth) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        labels.push({
          index,
          month: monthNames[month]
        });
        lastMonth = month;
      }
    });

    return labels;
  }, [attendanceGrid.dates]);

  return (
    <LayoutWrapper
      title="Venue Attendance Report"
      subtitle="Session attendance overview and analytics"
    >
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/reports')}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Reports
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Date Range</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-field w-full"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={fetchVenueAttendanceReport}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <CalendarIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Total Sessions</h4>
                </div>
              </div>
              <p className="text-3xl font-bold text-white">
                {reportData.sessionStats?.length || 0}
              </p>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                    <UserGroupIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Total Attendees</h4>
                </div>
              </div>
              <p className="text-3xl font-bold text-white">
                {reportData.totalAttendesForSessions || 0}
              </p>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <CurrencyDollarIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Tokens Spent</h4>
                </div>
              </div>
              <div>
                <p className="text-3xl font-bold text-white mb-2">
                  {reportData.totalTokensSpentForSessions || 0}
                </p>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((reportData.totalTokensSpentForSessions / 100) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                    <ClockIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Time in Sessions</h4>
                </div>
              </div>
              <div>
                <p className="text-3xl font-bold text-white mb-2">
                  {Math.floor((reportData.totalTimeSpentForSessions || 0) / 60)}h {(reportData.totalTimeSpentForSessions || 0) % 60}m
                </p>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min((reportData.totalTimeSpentForSessions / 1000) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Heatmap Grid - Dates across top, Hours down left */}
          <div className="card p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Session Attendance Heatmap</h3>
            <div className="overflow-x-auto">
              <div className="inline-flex flex-col gap-1">
                {/* Month labels */}
                <div className="flex gap-1 mb-1 ml-12">
                  {monthLabels.map((label, index) => (
                    <div
                      key={index}
                      className="text-xs text-gray-400"
                      style={{
                        position: 'absolute',
                        left: `${label.index * 14}px`,
                        marginLeft: '48px'
                      }}
                    >
                      {label.month}
                    </div>
                  ))}
                </div>

                {/* Day of week letters */}
                <div className="flex gap-1 mb-2 ml-12">
                  {attendanceGrid.dates.map((date, index) => {
                    const dayLetters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                    return (
                      <div
                        key={index}
                        className="w-3 h-3 text-xs text-gray-500 flex items-center justify-center"
                        title={date.toLocaleDateString()}
                      >
                        {dayLetters[date.getDay()]}
                      </div>
                    );
                  })}
                </div>

                {/* Grid with hours */}
                {attendanceGrid.hours.map((hour, hourIndex) => (
                  <div key={hour} className="flex gap-1 items-center">
                    {/* Hour label on left */}
                    <div className="w-10 text-xs text-gray-400 text-right pr-2">
                      {formatHour(hour)}
                    </div>

                    {/* Cells for each date */}
                    <div className="flex gap-1">
                      {attendanceGrid.grid[hourIndex].map((cell, dateIndex) => (
                        <div
                          key={dateIndex}
                          className={`w-3 h-3 rounded-sm ${getIntensityColor(cell.count)} cursor-pointer hover:ring-2 hover:ring-white transition-all`}
                          title={`${attendanceGrid.dates[dateIndex].toLocaleDateString()} ${formatHour(hour)}\nSessions: ${cell.count}\nAttendees: ${cell.attendees}\nTokens: ${cell.tokens}${cell.sessions.length > 0 ? '\n' + cell.sessions.map(s => s.sessionName).join(', ') : ''}`}
                        ></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="mt-4 flex items-center gap-2 text-xs text-gray-400">
                <span>Less</span>
                <div className="flex gap-1">
                  <div className="w-3 h-3 bg-gray-800 rounded-sm"></div>
                  <div className="w-3 h-3 bg-blue-900 rounded-sm"></div>
                  <div className="w-3 h-3 bg-blue-700 rounded-sm"></div>
                  <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                  <div className="w-3 h-3 bg-blue-400 rounded-sm"></div>
                </div>
                <span>More</span>
              </div>
            </div>
          </div>

          {/* Session Details Table */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Session Details</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Session Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Attendees
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Duration
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {reportData.sessionStats
                    ?.sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((session, index) => (
                      <tr key={index} className="hover:bg-gray-800/50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {new Date(session.date).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-medium">
                          {session.sessionName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {session.attendes}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {session.tokenSpent}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {parseTimeSpanToDisplay(session.sessionLength)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!reportData && !loading && (
        <div className="card p-12 text-center">
          <CalendarIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Select a date range and generate a report to view venue attendance data</p>
        </div>
      )}
    </LayoutWrapper>
  );
};

// Helper function to parse C# TimeSpan format (HH:MM:SS)
const parseTimeSpanToDisplay = (timeSpan) => {
  if (!timeSpan) return '0m';

  // Handle string format "HH:MM:SS" or "D.HH:MM:SS"
  const parts = timeSpan.split(':');
  if (parts.length === 3) {
    const dayParts = parts[0].split('.');
    const days = dayParts.length === 2 ? parseInt(dayParts[0], 10) : 0;
    const hours = dayParts.length === 2 ? parseInt(dayParts[1], 10) : parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  return '0m';
};

export default VenueAttendanceReport;

